import {
  FilterOptionKeys,
  GoalFilterDocument,
  GoalOverviewRoadmapViewOptions,
} from '@griply/types';
import {
  defaultGoalsOverviewFilterDocument,
  goalAndProjectFilterSections,
  goalAndProjectListFilters,
  goalFilterSections,
  GoalListFilterKey,
  goalListFilters,
} from '@griply/utils';
import {
  useGoalsOverviewFilterDocument,
  useGoalsTimelineFilterDocument,
  useUpdateGoalsOverviewFilterDocument,
  useUpdateGoalsTimelineFilterDocument,
} from 'features/user';
import React, { useCallback, useMemo } from 'react';
import { GoalListFilterButton } from 'shared/components/connected/goal-list-filter-button';

export type GoalsFilterButtonProps = {
  view: GoalOverviewRoadmapViewOptions;
};

export const GoalsFilterButton: React.FC<GoalsFilterButtonProps> = ({
  view,
}) => {
  const isTimeline = view === GoalOverviewRoadmapViewOptions.Timeline;

  const overviewFilterDocument = useGoalsOverviewFilterDocument();
  const timelineFilterDocument = useGoalsTimelineFilterDocument();
  const updateOverviewFilterDocument = useUpdateGoalsOverviewFilterDocument();
  const updateTimelineFilterDocument = useUpdateGoalsTimelineFilterDocument();

  // the Timeline shares the Goals list's sort mode (and drag order); grouping + filters stay its own
  const filterDocument = useMemo(
    () =>
      isTimeline
        ? {
            ...timelineFilterDocument,
            sortingMode: overviewFilterDocument.sortingMode,
          }
        : overviewFilterDocument,
    [isTimeline, timelineFilterDocument, overviewFilterDocument],
  );

  const updateFilterDocument = useCallback(
    (update: Partial<GoalFilterDocument>) => {
      if (!isTimeline) {
        updateOverviewFilterDocument(update);
        return;
      }
      // shared sort → goals-overview doc; grouping + filters → timeline doc
      const { sortingMode, ...rest } = update;
      if (sortingMode !== undefined) {
        updateOverviewFilterDocument({ sortingMode });
      }
      if (Object.keys(rest).length) {
        updateTimelineFilterDocument(rest);
      }
    },
    [isTimeline, updateOverviewFilterDocument, updateTimelineFilterDocument],
  );

  // the Timeline hides the goal-active info link
  const filterInfoUrlOverrides = useMemo(
    () => (isTimeline ? { [FilterOptionKeys.GoalActive]: null } : undefined),
    [isTimeline],
  );

  // only the standalone Timeline offers the Goal/Project "Type" filter
  const supportedFilters = useMemo<
    (GoalListFilterKey | FilterOptionKeys.GoalType)[]
  >(
    () => (isTimeline ? [...goalAndProjectListFilters] : [...goalListFilters]),
    [isTimeline],
  );

  // Reset clears only the filters; sorting and grouping are left untouched.
  const handleReset = () => updateFilterDocument({ filters: null });

  return (
    <GoalListFilterButton
      filterDocument={filterDocument}
      defaultFilterDocument={defaultGoalsOverviewFilterDocument}
      supportedFilters={supportedFilters}
      filterInfoUrlOverrides={filterInfoUrlOverrides}
      filterSections={
        isTimeline ? goalAndProjectFilterSections : goalFilterSections
      }
      onUpdate={updateFilterDocument}
      onReset={handleReset}
    />
  );
};
